import { useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

function getFocusables(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => !el.hasAttribute('aria-hidden'));
}

/**
 * Trap focus inside the modal while it's open.
 * Tab cycles within focusable elements; Esc calls onClose; restores focus on unmount.
 */
export function useFocusTrap(modalRef, isOpen, onClose) {
    useEffect(() => {
        if (!isOpen || !modalRef.current) return undefined;

        const previousFocus = document.activeElement;
        const root = modalRef.current;
        const focusables = getFocusables(root);
        if (focusables[0]) focusables[0].focus();

        const handleKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose?.();
                return;
            }
            if (e.key !== 'Tab') return;

            const live = getFocusables(root);
            if (live.length === 0) return;

            const firstLive = live[0];
            const lastLive = live[live.length - 1];

            if (e.shiftKey && document.activeElement === firstLive) {
                e.preventDefault();
                lastLive.focus();
            } else if (!e.shiftKey && document.activeElement === lastLive) {
                e.preventDefault();
                firstLive.focus();
            }
        };

        root.addEventListener('keydown', handleKey);
        return () => {
            root.removeEventListener('keydown', handleKey);
            if (previousFocus && typeof previousFocus.focus === 'function') {
                previousFocus.focus();
            }
        };
    }, [isOpen, modalRef, onClose]);
}
